import { makeAutoObservable, observable, runInAction } from 'mobx';
import axios from 'axios';
import urls from './Urls';
import { describeError } from './AudioCatalog';
import { expectedFilesField } from '../Components/Audio/bulkAudioForm';

/**
 * The bulk audio runs (SCRUM-1847): /api/ai/audio-jobs on the server.
 *
 *  - GET  /options      what a run can choose, what the server can do, the run that works now
 *  - POST /plan         a dry run: reads the dictionary and S3, creates and replaces nothing
 *  - POST /             starts a run - only with the number of files that the plan showed: expectedMissingFiles (mode fill-missing) or
 *                       expectedReplaceFiles (mode replace-with-backup)
 *  - GET  /             the latest runs
 *  - GET  /:id          one run;  GET /:id/items  its files
 *  - POST /:id/stop     POST /:id/resume
 * The server never deletes an audio file in a bulk run. In the mode fill-missing it only creates the files that are missing; in the mode
 * replace-with-backup it replaces files that exist, each one only after its old version was copied to the backup folder of the run.
 */

const base = () => urls.ai.audioJobs;
const withCredentials = { withCredentials: true };

/**
 * What to show for a request that failed: describeError, and what a bulk answer adds -
 *  errors        every problem of a rejected request (400)
 *  plan          the plan that the server made now, when it refused to start (409)
 *  runningJobId  the run that works, when a second one cannot start (409)
 */
export function describeJobError(err) {
    const data = err?.response?.data;
    const errors = Array.isArray(data?.errors) ? data.errors.map(String) : [];
    const general = describeError(err);
    return {
        ...general,
        detail: errors.length > 0 && data?.error ? String(data.error) : general.detail,
        errors,
        plan: data?.plan && typeof data.plan === 'object' ? data.plan : null,
        runningJobId: typeof data?.runningJobId === 'string' ? data.runningJobId : null,
    };
}

class BulkAudioStore {
    options = null;
    optionsLoading = false;
    optionsError = null;

    /** The last plan, and the key of the request that it was made for (see requestKey in bulkAudioForm.js) */
    plan = null;
    planKey = null;
    planning = false;
    planError = null;

    starting = false;
    startError = null;

    /** The latest runs (the newest first), or null until they are loaded */
    jobs = null;
    jobsLoading = false;
    jobsError = null;

    /** The run that a stop / resume is working on, and the error of the last one that failed */
    actionJobId = null;
    actionError = null;

    constructor() {
        makeAutoObservable(this, { options: observable.ref, plan: observable.ref, jobs: observable.shallow });
    }

    /**
     * The run that works now. Once the list of runs is loaded it is the only source (the run that GET /options showed at the first load
     * may have finished since); before that, the run that /options showed.
     */
    get activeJob() {
        if (this.jobs) return this.jobs.find((job) => job.status === 'running' && !job.stalled) ?? null;
        return this.options?.activeJob ?? null;
    }

    loadOptions = async () => {
        if (this.optionsLoading) return;
        this.optionsLoading = true;
        this.optionsError = null;
        try {
            const res = await axios.get(`${base()}/options`, withCredentials);
            if (!res.data?.limits || !Array.isArray(res.data?.dictionaries)) throw new Error('Unexpected answer of the server');
            runInAction(() => {
                this.options = res.data;
            });
        } catch (err) {
            runInAction(() => {
                this.optionsError = describeJobError(err);
            });
        } finally {
            runInAction(() => {
                this.optionsLoading = false;
            });
        }
    };

    /**
     * A dry run. Nothing is created.
     * @param {object} request the request of buildRequest
     * @param {string} key requestKey(request)
     * @returns {Promise<boolean>} true - there is a plan
     */
    planRun = async (request, key) => {
        if (this.planning) return false;
        this.planning = true;
        this.planError = null;
        this.startError = null;
        try {
            const res = await axios.post(`${base()}/plan`, request, withCredentials);
            if (!res.data?.plan?.files) throw new Error('Unexpected answer of the server');
            runInAction(() => {
                this.plan = res.data.plan;
                this.planKey = key;
            });
            return true;
        } catch (err) {
            runInAction(() => {
                this.plan = null;
                this.planKey = null;
                this.planError = describeJobError(err);
            });
            return false;
        } finally {
            runInAction(() => {
                this.planning = false;
            });
        }
    };

    /**
     * Starts the run. The server plans again, and starts only if the number of files is the one that the admin saw: the missing files
     * (mode fill-missing) or the files that would be replaced (mode replace-with-backup) - each mode has its own field in the request.
     * When it is not (or a blocker came up), the answer holds the new plan: it replaces the plan on the screen, and the admin confirms again.
     * @param {object} request the request of buildRequest
     * @param {string} key requestKey(request)
     * @param {number} expectedFiles the number of files that the plan showed (plannedFiles)
     * @returns {Promise<object|null>} the run, or null
     */
    startRun = async (request, key, expectedFiles) => {
        if (this.starting) return null;
        this.starting = true;
        this.startError = null;
        try {
            const res = await axios.post(base(), { ...request, [expectedFilesField(request)]: expectedFiles }, withCredentials);
            if (!res.data?.job?.id) throw new Error('Unexpected answer of the server');
            const { job } = res.data;
            runInAction(() => {
                this.jobs = [job, ...(this.jobs ?? []).filter((other) => other.id !== job.id)];
                // The plan was the plan of a run that started: it must not stay on the screen as if it could be started again
                this.plan = null;
                this.planKey = null;
            });
            return job;
        } catch (err) {
            const error = describeJobError(err);
            runInAction(() => {
                this.startError = error;
                if (error.plan?.files) {
                    this.plan = error.plan;
                    this.planKey = key;
                }
            });
            return null;
        } finally {
            runInAction(() => {
                this.starting = false;
            });
        }
    };

    loadJobs = async () => {
        if (this.jobsLoading) return;
        this.jobsLoading = true;
        try {
            const res = await axios.get(base(), withCredentials);
            if (!Array.isArray(res.data?.jobs)) throw new Error('Unexpected answer of the server');
            runInAction(() => {
                this.jobs = res.data.jobs;
                this.jobsError = null;
            });
        } catch (err) {
            runInAction(() => {
                this.jobsError = describeJobError(err);
            });
        } finally {
            runInAction(() => {
                this.jobsLoading = false;
            });
        }
    };

    /** Puts a run that the server sent into the list (in its place, or at the top) */
    replaceJob = (job) => {
        const jobs = this.jobs ?? [];
        this.jobs = jobs.some((other) => other.id === job.id)
            ? jobs.map((other) => (other.id === job.id ? job : other))
            : [job, ...jobs];
    };

    /** One run (null when it cannot be loaded; the error is in the second part) */
    fetchJob = async (id) => {
        try {
            const res = await axios.get(`${base()}/${id}`, withCredentials);
            if (!res.data?.job?.id) throw new Error('Unexpected answer of the server');
            runInAction(() => this.replaceJob(res.data.job));
            return { job: res.data.job, error: null };
        } catch (err) {
            return { job: null, error: describeJobError(err) };
        }
    };

    /** The files of a run: { items, total, skip, limit }, or { error } */
    fetchItems = async (id, { status = '', skip = 0, limit = 50 } = {}) => {
        try {
            const res = await axios.get(`${base()}/${id}/items`, { ...withCredentials, params: { status: status || undefined, skip, limit } });
            if (!Array.isArray(res.data?.items)) throw new Error('Unexpected answer of the server');
            return { ...res.data, error: null };
        } catch (err) {
            return { items: [], total: 0, skip, limit, error: describeJobError(err) };
        }
    };

    /** Asks a run to stop: the files that are being made are finished, no other file starts */
    stopJob = (id) => this.act(id, 'stop', {});

    /** Continues a run. body: { retryFailed, maxCostUsd } */
    resumeJob = (id, body = {}) => this.act(id, 'resume', body);

    act = async (id, action, body) => {
        if (this.actionJobId) return null;
        this.actionJobId = id;
        this.actionError = null;
        try {
            const res = await axios.post(`${base()}/${id}/${action}`, body, withCredentials);
            if (!res.data?.job?.id) throw new Error('Unexpected answer of the server');
            runInAction(() => this.replaceJob(res.data.job));
            return res.data.job;
        } catch (err) {
            runInAction(() => {
                this.actionError = describeJobError(err);
            });
            return null;
        } finally {
            runInAction(() => {
                this.actionJobId = null;
            });
        }
    };

    clearActionError = () => {
        this.actionError = null;
    };

    clearStartError = () => {
        this.startError = null;
    };
}

const bulkAudioStore = new BulkAudioStore();
export default bulkAudioStore;
